import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Copy, Loader2, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { smsHandyAPI } from '@/lib/api';
import { smsLaenge, smsLink } from '@/lib/sms';

/* SMS über das Vertriebshandy (Betreiber 06.10.2026).
   Das CRM verschickt nichts selbst: Der Knopf ist ein echter Link (sms:…), der die Nachrichten-App mit Nummer und
   Text öffnet. Am iPhone geht die SMS direkt raus, am iMac über die SMS-Weiterleitung des iPhones. Gesendet wird
   erst, wenn der Mensch dort auf Senden tippt.
   Nummer und Vorlagen kommen vom Server (Anrede-Regel für Kinder, Ortszeit, Kontaktsperre, Widerspruch). Beim Klick
   hält das CRM fest, dass die SMS an die Nachrichten-App übergeben wurde. Wer dort doch nicht sendet, nimmt den
   Eintrag zurück. Der Dialog bietet nie einen Link an, dessen Eintrag der Server sicher ablehnt. */
const fehlertext = (e, sonst) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : sonst);

export default function SmsDialog({ leadId, buchungId, anlass, onClose, onFertig }) {
  const [vorschlag, setVorschlag] = useState(null);
  const [fehler, setFehler] = useState('');
  const [vorlage, setVorlage] = useState('');
  const [text, setText] = useState('');
  // null: noch nicht geöffnet · { laeuft }: Eintrag unterwegs · { id }: steht im Verlauf · { fehler }: Eintrag fehlt
  const [uebergabe, setUebergabe] = useState(null);
  const [busy, setBusy] = useState(false);
  const unterwegs = useRef(false);        // ein Tipp = ein Eintrag, auch bei Doppeltipp

  useEffect(() => {
    if (!leadId) return undefined;
    let aktiv = true;
    unterwegs.current = false;
    setVorschlag(null); setFehler(''); setVorlage(''); setText(''); setUebergabe(null); setBusy(false);
    smsHandyAPI.vorschlag({ lead_id: leadId, buchung_id: buchungId || undefined, anlass: anlass || undefined })
      .then(r => {
        if (!aktiv) return;
        setVorschlag(r.data);
        const start = (r.data.vorlagen || []).find(v => v.id === r.data.empfohlen);
        if (start) { setVorlage(start.id); setText(start.text); }
      })
      .catch(e => {
        if (!aktiv) return;
        setFehler(e?.response?.status === 403 ? 'Für die SMS fehlt dir das Recht, diesen Lead zu bearbeiten.' : fehlertext(e, 'Die SMS konnte nicht vorbereitet werden.'));
      });
    return () => { aktiv = false; };
  }, [leadId, buchungId, anlass]);

  if (!leadId) return null;

  const sauber = text.trim();
  const laenge = smsLaenge(sauber);
  const vorlagen = vorschlag?.vorlagen || [];
  /* Hat die Person Werbung widersprochen, nimmt der Server nur einen Termin-Anlass an. Der Link wird deshalb erst
     frei, wenn eine der angebotenen Termin-Vorlagen gewählt ist — der Text darunter bleibt änderbar. */
  const anlassErlaubt = !vorschlag?.widerspruch || vorlagen.some(v => v.id === vorlage);
  const link = vorschlag?.nummer && sauber && anlassErlaubt ? smsLink(vorschlag.nummer, sauber) : null;

  // „Eigener Text" kennzeichnet nur: Was schon im Feld steht, bleibt stehen
  const waehlen = (v) => { setVorlage(v.id); if (v.text || !sauber) setText(v.text); };

  const protokollieren = () => {
    setUebergabe({ laeuft: true });
    smsHandyAPI.protokoll({ lead_id: leadId, text: sauber, anlass: vorlage || 'frei', buchung_id: vorschlag?.buchung_id || buchungId || null }, { timeout: 20000 })
      .then(r => { setUebergabe({ id: r.data.id }); onFertig?.(r.data); })
      .catch(e => setUebergabe({ fehler: fehlertext(e, 'Der Eintrag im Verlauf konnte nicht gespeichert werden.') }));
  };

  /* Läuft beim Klick auf den Link. Der Link öffnet die Nachrichten-App sofort (kein preventDefault, kein await:
     Safari öffnet fremde Apps nur direkt aus dem Klick heraus). Der Eintrag im Verlauf geht parallel raus. */
  const beimOeffnen = () => {
    if (unterwegs.current) return;
    unterwegs.current = true;
    protokollieren();
  };

  const dochNicht = async () => {
    if (!uebergabe?.id) { onClose(); return; }
    setBusy(true);
    try { await smsHandyAPI.zuruecknehmen(uebergabe.id); toast.message('Eintrag zurückgenommen'); onFertig?.(null); onClose(); }
    catch (e) { toast.error(fehlertext(e, 'Zurücknehmen fehlgeschlagen')); }
    setBusy(false);
  };

  const kopieren = async () => {
    try { await navigator.clipboard.writeText(sauber); toast.success('Text kopiert'); }
    catch { toast.error('Kopieren hat nicht geklappt. Bitte den Text von Hand markieren.'); }
  };

  const hinweisKasten = (testid, satz, ton = 'neutral') => (
    <div className="space-y-4 text-sm">
      <p className={`rounded-md border p-3 ${ton === 'achtung' ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20' : 'border-border bg-muted/40'}`} data-testid={testid}>{satz}</p>
      <div className="flex justify-end"><Button variant="outline" size="sm" onClick={onClose}>Schließen</Button></div>
    </div>
  );

  let inhalt;
  if (fehler) inhalt = hinweisKasten('sms-fehler', fehler);
  else if (!vorschlag) inhalt = <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> SMS wird vorbereitet…</p>;
  else if (uebergabe) {
    inhalt = (
      <div className="space-y-4 text-sm" data-testid="sms-uebergeben">
        <div className="rounded-md border border-border bg-muted/40 p-3 space-y-1">
          <p className="font-semibold flex items-center gap-1.5"><Smartphone className="w-4 h-4 text-blue-600" /> Die Nachrichten-App sollte jetzt offen sein</p>
          <p>Tippe dort auf Senden. Erst dann geht die SMS an {vorschlag.nummer} raus.</p>
        </div>
        {uebergabe.fehler ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2" data-testid="sms-eintrag-fehlt">
            <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-600" /> Der Eintrag im Verlauf fehlt</p>
            <p className="text-xs">{uebergabe.fehler}</p>
            <Button variant="outline" size="sm" onClick={protokollieren} data-testid="sms-nachtragen">Eintrag nachtragen</Button>
          </div>
        ) : uebergabe.id ? (
          <p className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5" data-testid="sms-eintrag-steht"><CheckCircle2 className="w-3.5 h-3.5" /> Im Verlauf des Leads vermerkt.</p>
        ) : (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Eintrag im Verlauf wird gespeichert…</p>
        )}
        <p className="text-xs text-muted-foreground">
          Ist keine Nachrichten-App aufgegangen? Am iMac muss am iPhone die SMS-Weiterleitung eingeschaltet sein. An Geräten ohne Nachrichten-App hilft „Text kopieren“.
          {link && <> <a href={link} className="underline" data-testid="sms-nochmal">Noch einmal öffnen</a></>}
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={kopieren} className="gap-1.5"><Copy className="w-3.5 h-3.5" /> Text kopieren</Button>
          <Button variant="outline" size="sm" onClick={dochNicht} disabled={busy || !!uebergabe.laeuft} data-testid="sms-doch-nicht">Doch nicht gesendet</Button>
          <Button size="sm" onClick={onClose} disabled={busy} data-testid="sms-fertig">Fertig</Button>
        </div>
      </div>
    );
  }
  else if (vorschlag.gesperrt) inhalt = hinweisKasten('sms-gesperrt', 'Diese Person möchte nicht mehr kontaktiert werden. Eine SMS ist hier nicht vorgesehen.', 'achtung');
  else if (vorschlag.nummer_falsch) inhalt = hinweisKasten('sms-nummer-falsch', `Die Nummer ${vorschlag.nummer_anzeige} ist am Lead als falsch vermerkt. Bitte trag zuerst über „Bearbeiten“ die richtige ein.`, 'achtung');
  else if (!vorschlag.nummer) inhalt = hinweisKasten('sms-ohne-nummer', vorschlag.nummer_anzeige
    ? `Aus „${vorschlag.nummer_anzeige}“ wird keine eindeutige Handynummer. Bitte korrigier sie über „Bearbeiten“, am besten mit +49 oder führender 0.`
    : 'Am Lead steht keine Handynummer. Bitte trag sie zuerst über „Bearbeiten“ ein.');
  else if (vorschlag.widerspruch && !vorlagen.length) inhalt = hinweisKasten('sms-nicht-erlaubt', 'Diese Person hat der Kontaktaufnahme zu Werbezwecken widersprochen und hat keinen anstehenden Termin. Eine SMS ist hier nicht vorgesehen.', 'achtung');
  else {
    inhalt = (
      <div className="space-y-4 text-sm">
        <div className="rounded-md border border-border bg-muted/40 p-3 space-y-0.5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">An</p>
          <p className="font-medium font-mono" data-testid="sms-nummer">{vorschlag.nummer}</p>
          {vorschlag.termin?.label && <p className="text-xs text-muted-foreground">Termin: {vorschlag.termin.label}</p>}
          {vorschlag.festnetz && <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="sms-festnetz">Das sieht nach einer Festnetznummer aus. Dort kommt eine SMS meist nicht an.</p>}
        </div>
        {vorschlag.widerspruch && (
          <p className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs" data-testid="sms-widerspruch">Diese Person hat Werbung widersprochen. Schreib ihr nur zu ihrem gebuchten Termin und wähl dafür zuerst eine der beiden Termin-Vorlagen. Den Text kannst du danach anpassen.</p>
        )}
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Vorlage</p>
          <div className="flex flex-wrap gap-1.5" data-testid="sms-vorlagen">
            {vorlagen.map(v => (
              <button key={v.id} type="button" onClick={() => waehlen(v)} data-testid={`sms-vorlage-${v.id}`} aria-pressed={vorlage === v.id}
                      className={`rounded-full border px-3 py-1 text-xs ${vorlage === v.id ? 'border-primary bg-primary/10 font-medium' : 'border-border hover:bg-muted/40'}`}>
                {v.name}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <Textarea value={text} onChange={e => setText(e.target.value)} rows={6} maxLength={1000}
                    placeholder={vorschlag.widerspruch ? 'Zuerst eine Termin-Vorlage wählen' : 'Vorlage wählen oder eigenen Text schreiben'} data-testid="sms-text" />
          <p className={`text-xs ${laenge.teile > 1 || laenge.unicode ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`} data-testid="sms-laenge">
            {laenge.zeichen} Zeichen{laenge.teile > 0 ? `, ${laenge.teile === 1 ? 'eine SMS' : `${laenge.teile} SMS am Stück`}` : ''}
            {laenge.unicode ? '. Enthält Emoji oder Sonderzeichen, dadurch passen nur 70 Zeichen in eine SMS.' : ''}
          </p>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="sms-hinweis">Das CRM verschickt nichts selbst. Der Knopf öffnet die Nachrichten-App mit Nummer und Text, dort tippst du auf Senden. Die SMS geht von der Nummer des Geräts raus, an dem du gerade arbeitest. Nimm dafür das Vertriebshandy oder den iMac, der damit verknüpft ist, kein privates Handy. Schreib per SMS nur zu Terminen und zu Anfragen der Person selbst. Werbung per SMS braucht eine Einwilligung.</p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Abbrechen</Button>
          <Button variant="ghost" size="sm" onClick={kopieren} disabled={!sauber} className="gap-1.5"><Copy className="w-3.5 h-3.5" /> Text kopieren</Button>
          {link ? (
            <Button asChild size="sm" className="gap-1.5">
              {/* Mittelklick zählt mit; das Kontextmenü würde den Link am Eintrag vorbei öffnen */}
              <a href={link} onClick={beimOeffnen} onAuxClick={beimOeffnen} onContextMenu={e => e.preventDefault()} data-testid="sms-oeffnen"><Smartphone className="w-4 h-4" /> In Nachrichten öffnen</a>
            </Button>
          ) : (
            <Button size="sm" disabled className="gap-1.5" data-testid="sms-oeffnen-gesperrt"><Smartphone className="w-4 h-4" /> In Nachrichten öffnen</Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Dialog open onOpenChange={o => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="sms-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-blue-600" /> SMS{vorschlag?.name ? ` an ${vorschlag.name}` : ''}</DialogTitle></DialogHeader>
        {inhalt}
      </DialogContent>
    </Dialog>
  );
}
